import React, { useState } from 'react';
import { CalendarDays, MapPin, ShoppingBag, Users, Activity, Lock, Star, MessageCircle } from 'lucide-react';
import { AdminBookings } from './AdminBookings';
import { AdminSedeConfig } from './AdminSedeConfig';
import { AdminProducts } from './AdminProducts'; // Admin products component
import { AdminStaff } from './AdminStaff';
import { AdminActivityLog } from './AdminActivityLog';

import { Player, Config } from '../../types';

interface AdminConfiguracionProps {
  userId: string;
  userRole?: string;
  players: Player[];
  db: any;
  getCollectionPath: (col: string) => string;
  config: Config;
  highlightBookingRequestId?: string | null;
  onHighlightBookingRequestHandled?: () => void;
  onOpenChatWithPlayer?: (playerId: string, playerName: string, playerPhoto: string) => void;
  subscriptionPlan?: 'Basico' | 'Full' | 'Full Plus';
}

export function AdminConfiguracion({
  userId,
  userRole = 'admin',
  players,
  db,
  getCollectionPath,
  config,
  highlightBookingRequestId,
  onHighlightBookingRequestHandled,
  onOpenChatWithPlayer,
  subscriptionPlan = 'Basico'
}: AdminConfiguracionProps) {
  // Por defecto se muestra la subpestaña de "Personalización"
  const [activeSubTab, setActiveSubTab] = useState<'reservas' | 'personalizacion' | 'productos' | 'personal' | 'actividad'>('reservas');

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      
      {/* SELECTOR DE SUBPESTAÑAS ESTILO PILL PREMIUM CENTRADO (SIN TÍTULOS PRINCIPALES) */}
      <div className="flex justify-center border-b border-white/5 pb-6">
        <div className="flex flex-wrap justify-center items-center gap-1.5 bg-black/40 border border-white/10 rounded-2xl p-1.5 w-full sm:w-auto">
          <button
            onClick={() => setActiveSubTab('reservas')}
            className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all duration-300 ${
              activeSubTab === 'reservas'
                ? 'theme-primary-bg text-black shadow-[0_4px_15px_rgba(var(--glow-rgb),0.3)] scale-[1.02]'
                : 'text-white/60 hover:text-white hover:bg-white/5'
            }`}
          >
            <CalendarDays size={14} className="shrink-0" />
            <span className="hidden sm:inline">Ajustes de Reservas</span>
            <span className="sm:hidden">Reservas</span>
          </button>


          <button
            onClick={() => setActiveSubTab('personalizacion')}
            className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all duration-300 ${
              activeSubTab === 'personalizacion'
                ? 'theme-primary-bg text-black shadow-[0_4px_15px_rgba(var(--glow-rgb),0.3)] scale-[1.02]'
                : 'text-white/60 hover:text-white hover:bg-white/5'
            }`}
          >
            <MapPin size={14} className="shrink-0" />
            <span className="hidden sm:inline">Personalización</span>
            <span className="sm:hidden">Sede</span>
          </button>

          <button
            onClick={() => setActiveSubTab('productos')}
            className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all duration-300 ${
              activeSubTab === 'productos'
                ? 'theme-primary-bg text-black shadow-[0_4px_15px_rgba(var(--glow-rgb),0.3)] scale-[1.02]'
                : 'text-white/60 hover:text-white hover:bg-white/5'
            }`}
          >
            <ShoppingBag size={14} className="shrink-0" />
            <span className="hidden sm:inline">Mis Productos</span>
            <span className="sm:hidden">Productos</span>
          </button>

          {userRole === 'admin' && (
            <button
              onClick={() => setActiveSubTab('personal')}
              className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all duration-300 ${
                activeSubTab === 'personal'
                  ? 'theme-primary-bg text-black shadow-[0_4px_15px_rgba(var(--glow-rgb),0.3)] scale-[1.02]'
                  : 'text-white/60 hover:text-white hover:bg-white/5'
              }`}
            >
              <Users size={14} className="shrink-0" />
              <span className="hidden sm:inline">Personal y Cuentas</span>
              <span className="sm:hidden">Cuentas</span>
            </button>
          )}

          <button
            onClick={() => setActiveSubTab('actividad')}
            className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all duration-300 ${
              activeSubTab === 'actividad'
                ? 'theme-primary-bg text-black shadow-[0_4px_15px_rgba(var(--glow-rgb),0.3)] scale-[1.02]'
                : 'text-white/60 hover:text-white hover:bg-white/5'
            }`}
          >
            <Activity size={14} className="shrink-0" />
            <span className="hidden sm:inline">Registro de Actividad</span>
            <span className="sm:hidden">Actividad</span>
          </button>
        </div>
      </div>

      {/* CONTENIDO DIVERGENTE SEGÚN SUBPESTAÑA */}
      <div className="transition-all duration-300">
        {activeSubTab === 'actividad' ? (
          <div className="animate-in fade-in duration-300">
            <AdminActivityLog db={db} getCollectionPath={getCollectionPath} userId={userId} />
          </div>
        ) : activeSubTab === 'personal' ? (
          <div className="animate-in fade-in duration-300">
            {subscriptionPlan === 'Basico' ? (
              <div className="flex flex-col items-center justify-center min-h-[400px] text-center p-6 bg-white/5 rounded-3xl border border-white/10 mt-4">
                <div className="w-20 h-20 bg-theme-primary/10 rounded-full flex items-center justify-center mb-6 border border-theme-primary/20">
                  <Lock size={32} className="theme-primary-text" />
                </div>
                <h2 className="text-2xl font-black uppercase tracking-tight text-white mb-2">
                  Gestión de Personal Bloqueada
                </h2>
                <p className="text-white/60 text-sm max-w-md mb-8">
                  La creación de cuentas para co-administradores, gestores de turnos y empleados está disponible a partir del <strong>Plan Full</strong>.
                </p>
                <button onClick={() => alert('Próximamente: Redirección a pasarela de pago')} className="theme-primary-bg text-black font-black uppercase tracking-widest text-xs px-8 py-4 rounded-xl hover:scale-105 transition-all flex items-center gap-2 shadow-lg shadow-theme-primary/20">
                  <Star size={16} /> Mejorar al Plan Full
                </button>
              </div>
            ) : (
              <AdminStaff db={db} getCollectionPath={getCollectionPath} players={players} userId={userId} />
            )}
          </div>
        ) : activeSubTab === 'reservas' ? (
          <div className="animate-in fade-in duration-300">
            <AdminBookings
              userId={userId}
              players={players}
              db={db}
              getCollectionPath={getCollectionPath}
              config={config}
              onlySetup={true}
              highlightRequestId={highlightBookingRequestId}
              onHighlightHandled={onHighlightBookingRequestHandled}
              onOpenChatWithPlayer={onOpenChatWithPlayer}
              subscriptionPlan={subscriptionPlan}
            />
          </div>
        ) : activeSubTab === 'productos' ? (
          <div className="animate-in fade-in duration-300">
            <AdminProducts db={db} getCollectionPath={getCollectionPath} userId={userId} />
          </div>

        ) : (
          <div className="animate-in fade-in duration-300">
            <AdminSedeConfig 
              userId={userId} 
              db={db} 
              getCollectionPath={getCollectionPath}
              config={config}
            />
          </div>
        )}
      </div>

    </div>
  );
}

